import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  Settings,
  Users,
  Plug,
  KeyRound,
  LucideIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/authContext";
import { useFeatureFlagsReady } from "../../contexts/launchDarklyContext";
import { useOptroIntegrationsHidden } from "../../hooks/useOptroIntegrationsHidden";
import { cn } from "../../lib/utils";
import { hasPermission } from "../../lib/permissions";
import { PermissionId } from "../../models/permission";

interface AdminSidebarProps {
  isCollapsed: boolean;
}

interface AdminLink {
  path: string;
  label: string;
  icon: LucideIcon;
  requiredPermission: PermissionId;
}

const adminLinks: AdminLink[] = [
  {
    path: "/admin/workspace_members",
    label: "Users",
    icon: Users,
    requiredPermission: "workspace_management",
  },
  {
    path: "/admin/roles",
    label: "Roles",
    icon: KeyRound,
    requiredPermission: "workspace_management",
  },
  {
    path: "/admin/integrations",
    label: "Integrations",
    icon: Plug,
    requiredPermission: "workspace_management",
  },
  {
    path: "/admin/settings",
    label: "Settings",
    icon: Settings,
    requiredPermission: "manage_approvals",
  },
];

export function AdminSidebar({ isCollapsed }: AdminSidebarProps) {
  const { workspaceMember } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const { pathname: currentPath } = useLocation();

  const integrationsHidden = useOptroIntegrationsHidden();
  // Until LaunchDarkly flags are authoritative, keep the Integrations link hidden —
  // mirroring RequireIntegrationsVisible's flagsReady gate — so an Optro session
  // doesn't briefly flash the legacy Integrations link before the flag resolves.
  const flagsReady = useFeatureFlagsReady();
  const hideIntegrations = !flagsReady || integrationsHidden;
  const visibleAdminLinks = useMemo(
    () =>
      adminLinks.filter(
        (link) =>
          hasPermission(workspaceMember, link.requiredPermission) &&
          !(hideIntegrations && link.path === "/admin/integrations"),
      ),
    [workspaceMember, hideIntegrations],
  );

  if (visibleAdminLinks.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "flex items-center p-3 text-sm hover:bg-stone-50 transition-colors duration-200 border border-transparent w-full",
          isCollapsed ? "justify-center" : "",
        )}
      >
        <div
          className={cn(
            "flex items-center min-w-0 flex-1",
            isCollapsed ? "justify-center gap-0" : "gap-2",
          )}
        >
          <div className="flex-shrink-0">
            <Settings size={20} />
          </div>
          <span
            className={cn(
              "truncate whitespace-nowrap transition-all duration-300",
              isCollapsed
                ? "w-0 opacity-0 overflow-hidden"
                : "w-auto opacity-100",
            )}
          >
            Admin
          </span>
        </div>
        {!isCollapsed && (
          <ChevronRight
            size={16}
            className={cn(
              "transition-transform duration-200 text-stone-400",
              isExpanded ? "rotate-90" : "",
            )}
          />
        )}
      </button>

      {!isCollapsed && isExpanded && (
        <div className="flex flex-col">
          {visibleAdminLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "flex items-center gap-2 pl-10 pr-3 py-2 text-sm hover:bg-stone-50 transition-colors duration-200 border border-transparent",
                currentPath.startsWith(link.path)
                  ? "bg-stone-50 border-stone-200"
                  : "",
              )}
            >
              <link.icon size={16} />
              <span className="truncate">{link.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
